import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, Trash2, Star, CheckCircle, Pencil } from "lucide-react";
import type { Address, ContactAddress, AccountAddress } from "@shared/schema";

type JunctionRecord = ContactAddress | AccountAddress;

interface AddressCardProps {
  address: Address;
  junction: JunctionRecord;
  onDelete?: () => void;
  onEdit?: () => void;
  onSetPrimary?: () => void;
  isPrimary?: boolean;
  "data-testid"?: string;
}

const addressTypeLabels: Record<string, string> = {
  residential: "Residential",
  postal: "Postal",
  registered_office: "Registered Office",
  principal_place_of_business: "Principal Place of Business",
  property: "Property",
  security: "Security",
};

export function AddressCard({
  address,
  junction,
  onDelete,
  onEdit,
  onSetPrimary,
  isPrimary = false,
  "data-testid": testId,
}: AddressCardProps) {
  const formatAddress = () => {
    if (address.formattedAddress) {
      return address.formattedAddress;
    }

    const parts: string[] = [];
    
    if (address.unitNumber) {
      parts.push(`${address.unitNumber}/`);
    }
    if (address.streetNumber) {
      parts.push(address.streetNumber);
    }
    if (address.streetName) {
      parts.push(address.streetName);
    }
    if (address.streetType) {
      parts.push(address.streetType);
    }
    
    const line1 = parts.join(" ");
    
    const line2Parts: string[] = [];
    if (address.suburb) {
      line2Parts.push(address.suburb);
    }
    if (address.state) {
      line2Parts.push(address.state);
    }
    if (address.postcode) {
      line2Parts.push(address.postcode);
    }
    
    return [line1, line2Parts.join(" ")].filter(Boolean).join(", ");
  };

  const addressType = junction.addressType as keyof typeof addressTypeLabels;

  return (
    <Card className="hover-elevate" data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <MapPin className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Badge variant="outline" className="text-xs">
                  {addressTypeLabels[addressType] || addressType}
                </Badge>
                {isPrimary && (
                  <Badge variant="default" className="text-xs">
                    <Star className="h-3 w-3 mr-1" />
                    Primary
                  </Badge>
                )}
                {address.isVerified === 1 && (
                  <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                )}
              </div>
              <p className="text-sm mt-1 break-words" data-testid="text-address-formatted">
                {formatAddress()}
              </p>
              {junction.notes && (
                <p className="text-xs text-muted-foreground mt-1">
                  {junction.notes}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onEdit}
                title="Edit address"
                data-testid="button-edit-address"
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            {!isPrimary && onSetPrimary && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={onSetPrimary}
                title="Set as primary"
                data-testid="button-set-primary-address"
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
            )}
            {onDelete && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-destructive hover:text-destructive"
                onClick={onDelete}
                title="Remove address"
                data-testid="button-delete-address"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
